/**
 * Product Details Carousel swiper implementation
 */

import { Box, Divider, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import _Swiper from 'swiper';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';
import { EffectFade, FreeMode, Pagination, Thumbs, Zoom } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useVisibleSlides } from '@/components/blocks/ProductCarousel/utils/useVisibleSlides';
import { ProductImage } from '@/components/blocks/ProductImage';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { Slide as ProductSlideType } from '@/data/types/Slide';
import { mergeSx } from '@/utils/mergeSx';

import {
	buttonNavigationNextSx,
	buttonNavigationPrevSx,
	portalImageSx,
	rootSx,
	swiperThumbsDesktopStyle,
	swiperThumbsMobileOrTabletStyle,
	thumbnailImageBottomLinePlaceholderSx,
	thumbnailImageBottomLineSx,
	thumbnailImageContainerSx,
	thumbnailImageSelectedSx,
	thumbnailImageSx,
	thumbnailNavContainerSx,
	zoomMessageContainerSx,
	zoomOutButtonSx,
} from '@/components/blocks/ProductDetails/styles/carousel';
import { useLocalization } from '@/data/Localization';
import { CUSTOM_BREAKPOINT_980 } from 'shared/custom/constants/commonConstants';

export type CarouselProps = {
	slides: ProductSlideType[];
	choose: (idx: number) => void;
	index?: number;
};

export type ProductDetailsCarouselRef = {
	goToNext: () => void;
	goToPrev: () => void;
};

type CustomCarouselProps = {
	slides: ProductSlideType[];
	visibleThumbs?: number;
	gtmSelectItem?: () => void;
	carouselRef?: React.RefObject<ProductDetailsCarouselRef>;
	onTotalSlidesChange?: (totalSlides: number) => void;
	onCurrentSlideChange?: (currentSlide: number) => void;
};

const IMAGE_PORTAL_ID = 'mediaImageZoom';
const ZOOM_CONTAINER_CLASS_NAME = 'swiper-zoom-container';

export const ProductDetailsCarousel = ({
	slides: initialSlides,
	visibleThumbs = 6,
	gtmSelectItem,
	carouselRef,
	onTotalSlidesChange,
	onCurrentSlideChange,
}: CustomCarouselProps) => {
	const localization = useLocalization('HDMProductDetail');
	const { lgMdMatch: isMobileOrTablet } = useVisibleSlides();

	const swiperModules = [Zoom, Pagination, Thumbs, FreeMode, EffectFade];
	const [swiper, setSwiper] = useState<_Swiper | null>(null);
	const [thumbsSwiper, setThumbsSwiper] = useState<_Swiper | null>(null);

	const [slides, setSlides] = useState(() => initialSlides.filter((s) => s.fullImage));
	const [currentSlide, setCurrentSlide] = useState(0);
	const [isZoomedIn, setIsZoomedIn] = useState(false);
	const [thumbFallbackToFullImage, setThumbFallbackToFullImage] = useState<Set<string>>(new Set());

	useEffect(() => {
		setSlides(initialSlides.filter((s) => s.fullImage));
		setCurrentSlide(0);
	}, [initialSlides]);

	useEffect(() => {
		onTotalSlidesChange?.(slides.length);
	}, [slides.length, onTotalSlidesChange]);

	useEffect(() => {
		onCurrentSlideChange?.(currentSlide);
	}, [currentSlide, onCurrentSlideChange]);

	const onSlideChange = (swiper: _Swiper) => {
		setCurrentSlide(swiper.realIndex);
	};

	const onZoomChange = (swiper: _Swiper, scale: number) => {
		const isZoomedIn = scale > 1;
		setIsZoomedIn(isZoomedIn);
		swiper.allowSlideNext = !isZoomedIn;
		swiper.allowSlidePrev = !isZoomedIn;
	};

	const onZoomOut = () => {
		if (!swiper) return;
		swiper.zoom.toggle();
	};

	const onClickSwiper = (swiper: _Swiper, event: MouseEvent | TouchEvent | PointerEvent) => {
		if (!isMobileOrTablet) return;

		const target = event.target;
		if (!(target instanceof HTMLElement)) return;
		if (!target.closest(`.${ZOOM_CONTAINER_CLASS_NAME}`)) return;

		swiper.zoom.toggle(event);
	};

	const onResizeSwiper = (swiper: _Swiper) => {
		if (typeof window === 'undefined') return;

		const windowWidth = window.innerWidth;
		if (windowWidth < CUSTOM_BREAKPOINT_980) return;

		swiper.zoom.out();
	};

	const goToPrev = useCallback(() => {
		swiper?.slidePrev();
	}, [swiper]);

	const goToNext = useCallback(() => {
		swiper?.slideNext();
	}, [swiper]);

	useImperativeHandle(
		carouselRef,
		() => ({
			goToNext,
			goToPrev,
		}),
		[goToNext, goToPrev]
	);

	const getThumbsStep = useCallback(() => {
		const visible = thumbsSwiper ? Math.floor(thumbsSwiper.slidesPerViewDynamic()) : visibleThumbs;
		return Math.max(visible || visibleThumbs, 1);
	}, [thumbsSwiper, visibleThumbs]);

	const pageThumbs = useCallback(
		(direction: 1 | -1) => {
			if (!thumbsSwiper) return;

			const lastIndex = thumbsSwiper.slides.length - 1;
			if (lastIndex < 0) return;

			const indexBefore = thumbsSwiper.activeIndex;
			const target = indexBefore + direction * getThumbsStep();
			thumbsSwiper.slideTo(Math.min(Math.max(target, 0), lastIndex));

			const indexAfter = thumbsSwiper.activeIndex;
			const hasMoved = indexAfter !== indexBefore;
			const selectedIndex = hasMoved ? indexAfter : direction === 1 ? lastIndex : 0;

			swiper?.slideToLoop(selectedIndex);
		},
		[thumbsSwiper, swiper, getThumbsStep]
	);

	const goToThumbsPrev = useCallback(() => pageThumbs(-1), [pageThumbs]);
	const goToThumbsNext = useCallback(() => pageThumbs(1), [pageThumbs]);

	const onImageError = (failedSrc: string) => {
		setSlides((prevSlides) => {
			const newSlides = prevSlides.filter((s) => s.fullImage !== failedSrc);
			if (newSlides.length === prevSlides.length) return prevSlides;
			setCurrentSlide((prevCurrentSlide) =>
				Math.max(Math.min(prevCurrentSlide, newSlides.length - 1), 0)
			);
			return newSlides;
		});
	};

	const onThumbnailImageError = (slide: ProductSlideType, triedSrc: string) => {
		if (triedSrc === slide.fullImage) {
			onImageError(slide.fullImage);
			return;
		}
		setThumbFallbackToFullImage((prev) => new Set(prev).add(slide.fullImage));
	};

	const currentSlideRef = useRef(currentSlide);
	currentSlideRef.current = currentSlide;
	const prevSlidesLengthRef = useRef(slides.length);
	useEffect(() => {
		if (prevSlidesLengthRef.current === slides.length) return;
		prevSlidesLengthRef.current = slides.length;
		if (!swiper) return;
		swiper.update();
		if (swiper.params.loop) {
			swiper.loopDestroy();
			swiper.loopCreate();
		}
		swiper.slideToLoop(currentSlideRef.current, 0);
	}, [slides, swiper]);

	return (
		<Box sx={rootSx}>
			<Box position="relative">
				<Box id={IMAGE_PORTAL_ID} sx={portalImageSx} onClick={gtmSelectItem} />

				<Swiper
					id="mediaImageMainDisplay"
					slidesPerView={1}
					modules={swiperModules}
					pagination={
						isMobileOrTablet
							? {
									clickable: true,
									renderBullet: (index, className) =>
										`<span class="${className}" data-image-index="${index}"></span>`,
							  }
							: false
					}
					onPaginationRender={(_swiper, el) => {
						el.id = 'mediaImageNavDots';
						el.classList.add('media-image__navigation-dot');
					}}
					thumbs={{ swiper: thumbsSwiper }}
					zoom={{
						toggle: false, // Disable default double tap zoom
					}}
					loop
					onSwiper={setSwiper}
					onSlideChange={onSlideChange}
					onClick={onClickSwiper}
					onZoomChange={onZoomChange}
					onResize={onResizeSwiper}
					effect="fade"
					fadeEffect={{
						crossFade: true,
					}}
					speed={500}
				>
					{slides.map((slide, i) => (
						<SwiperSlide key={i}>
							{/* eslint-disable-next-line react/forbid-component-props */}
							<Box className={ZOOM_CONTAINER_CLASS_NAME}>
								<ProductImage
									src={slide.fullImage}
									alt={slide.name}
									canZoom={!isMobileOrTablet}
									sx={{ width: '100%', height: 'auto' }}
									onError={() => onImageError(slide.fullImage)}
									imagePortalId={IMAGE_PORTAL_ID}
									width={600}
									height={600}
									loading={i === 0 ? 'eager' : 'lazy'}
									fetchPriority={i === 0 ? 'high' : undefined}
									priority={i === 0}
								/>
							</Box>
						</SwiperSlide>
					))}

					{isMobileOrTablet ? (
						<>
							<Box sx={zoomMessageContainerSx} id={'mediaImageGalleryZoom'}>
								<DynamicIcon name={isZoomedIn ? 'swipe' : 'touchApp'} color="white" />
								<Typography color="white">
									{isZoomedIn ? localization.SwipeToSeeMore.t() : localization.TouchToZoom.t()}
								</Typography>
							</Box>

							{isZoomedIn ? (
								<IconButton sx={zoomOutButtonSx} onClick={onZoomOut}>
									<DynamicIcon name="close" color="white" />
								</IconButton>
							) : null}
						</>
					) : null}
				</Swiper>
			</Box>

			<Box sx={thumbnailNavContainerSx}>
				<Swiper
					id="mediaImageThumbnailNav"
					modules={swiperModules}
					freeMode
					slidesPerView={visibleThumbs}
					watchSlidesProgress
					onSwiper={setThumbsSwiper}
					spaceBetween={15}
					// eslint-disable-next-line react/forbid-component-props
					style={isMobileOrTablet ? swiperThumbsMobileOrTabletStyle : swiperThumbsDesktopStyle}
				>
					{slides.map((slide, i) => {
						const isActive = i === currentSlide;
						const thumbSrc = thumbFallbackToFullImage.has(slide.fullImage)
							? slide.fullImage
							: slide.thumbnail || slide.fullImage;

						return (
							<SwiperSlide key={i}>
								<Box
									sx={thumbnailImageContainerSx}
									// eslint-disable-next-line react/forbid-component-props
									className={
										isActive
											? 'media-image__thumbnail media-image__thumbnail--selected'
											: 'media-image__thumbnail'
									}
									id={'mediaImageThumbnailNav'}
									data-image-index={i}
								>
									<ProductImage
										src={thumbSrc}
										alt={slide.name}
										sx={mergeSx(thumbnailImageSx, isActive && thumbnailImageSelectedSx)}
										width={90}
										height={90}
										loading="lazy"
										onError={() => onThumbnailImageError(slide, thumbSrc)}
									/>
									<Divider
										sx={
											isActive ? thumbnailImageBottomLineSx : thumbnailImageBottomLinePlaceholderSx
										}
									/>
								</Box>
							</SwiperSlide>
						);
					})}

					<IconButton
						id="mediaImageThumbnailsControlPrev"
						sx={buttonNavigationPrevSx}
						onClick={goToThumbsPrev}
					>
						<DynamicIcon name="chevronLeft" color="white" />
					</IconButton>

					<IconButton
						id="mediaImageThumbnailsControlNext"
						sx={buttonNavigationNextSx}
						onClick={goToThumbsNext}
					>
						<DynamicIcon name="chevronRight" color="white" />
					</IconButton>
				</Swiper>
			</Box>
		</Box>
	);
};
